import { faChevronDown } from "@jewlr/pro-regular-svg-icons/faChevronDown"
import { faChevronUp } from "@jewlr/pro-regular-svg-icons/faChevronUp"
import { faSpinner } from "@jewlr/pro-regular-svg-icons/faSpinner"
import { faTimes } from "@jewlr/pro-regular-svg-icons/faTimes"
import PropTypes from "prop-types"
import { Suspense, useCallback, useState } from "react"
import { useDispatch } from "react-redux"

import { ItemAction, ItemTitle, LineItemCard } from "areas/cart/components"
import {
  CardProductImage,
  CardProductImageContainer,
  RemoveItem,
} from "areas/cart/line-item/components"
import Price from "areas/cart/line-item/jewlr/price"
import { removeAddon } from "areas/cart/store/actions"
import Expand from "components/ui/expand"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import { jewlr_t } from "helpers/application"
import { getImgSrcSet } from "helpers/image"

const AddonDesktopItem = ({
  addon,
  index,
  removingItemId,
  setOnRemoveAction,
}) => {
  const [showAddonDetail, toggleAddonDetail] = useState(false)
  const dispatch = useDispatch()
  const isRemoving = removingItemId === addon?.id

  const handleRemoveClick = useCallback(() => {
    if (addon) {
      setOnRemoveAction({
        itemId: addon.id,
        loading: false,
        onConfirm: (callbacks = {}) =>
          dispatch(removeAddon(addon, null, callbacks)),
      })
    }
  }, [addon, dispatch, setOnRemoveAction])

  if (!addon) return null

  return (
    <LineItemCard
      data-cy={`cart-addon-item-${index}-${addon.addon_code}`}
      data-ge-basket-cartitemid={addon.id}
      id={"addon_item_" + addon.id}
      key={`d_addon-${addon.id}`}
    >
      <div className="desktop:gap-24 relative flex gap-16">
        <CardProductImageContainer>
          <CardProductImage
            alt=""
            fallback={addon.image_url}
            height="1024"
            sizes="(min-width: 1024px) 130px, 120px"
            srcSet={getImgSrcSet(
              addon.image_url.replace(
                sl.config.jewlr_asset_url,
                `${sl.config.jewlr_imgsmbly_url}assets/`
              ),
              1024,
              {
                sizes: [130, 260],
              }
            )}
            title={addon.title}
            width="1024"
          />
        </CardProductImageContainer>
        <div className="flex-1">
          <ItemTitle>{addon.title}</ItemTitle>
          <Price item={addon} type="addon" />
        </div>
        <RemoveItem
          aria-busy={isRemoving}
          aria-label={
            isRemoving
              ? `Removing ${addon.title} from ${jewlr_t("Cart")}...`
              : `Remove ${addon.title} from ${jewlr_t("Cart")}`
          }
          data-cy={`cart-addon-remove-btn-${index}-${addon.addon_code}`}
          disabled={isRemoving}
          onClick={handleRemoveClick}
        >
          <FontAwesomeIcon
            icon={isRemoving ? faSpinner : faTimes}
            spin={isRemoving}
          />
        </RemoveItem>
      </div>
      <div className="mt-24 flex items-start justify-between">
        <div>
          <ItemAction
            aria-controls={`cart-addon-details-${addon.id}`}
            aria-expanded={showAddonDetail}
            id={`cart-addon-details-toggle-${addon.id}`}
            onClick={() => toggleAddonDetail(!showAddonDetail)}
          >
            Item Details
            <FontAwesomeIcon
              className="text-10 ml-4"
              icon={showAddonDetail ? faChevronUp : faChevronDown}
            />
          </ItemAction>
          {typeof window !== "undefined" && (
            <Suspense fallback={<div />}>
              <Expand
                id={`cart-addon-details-${addon.id}`}
                show={showAddonDetail}
              >
                <div className="mt-8">
                  <p className="text-13 font-medium">{addon.description}</p>
                </div>
              </Expand>
            </Suspense>
          )}
        </div>
      </div>
    </LineItemCard>
  )
}

AddonDesktopItem.propTypes = {
  addon: PropTypes.object,
  index: PropTypes.number,
  removingItemId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setOnRemoveAction: PropTypes.func,
}

export default AddonDesktopItem
